import { useEffect, useMemo, useRef, useState } from 'react'
import { DEFAULT_SSE_HEADERS, type SSEStream, useSSEStream, type UseSSEStreamOptions } from './use-sse-stream.ts'

/** How long a stream first waits to reconnect after the connection closed before its record finished. */
export const SSE_STREAM_RECONNECT_MS = 2_000

/** The longest a stream waits between reconnects; the wait doubles up to it while nothing new arrives. */
export const SSE_STREAM_MAX_RECONNECT_MS = 30_000

/** How many reconnects in a row may bring nothing new before the stream is given up as ended. */
export const SSE_STREAM_MAX_IDLE_RECONNECTS = 5

/** The wait before the next reconnect, doubling with each reconnect in a row that brought nothing new. */
export const getSSEStreamReconnectDelay = (idleReconnects: number) =>
  Math.min(SSE_STREAM_RECONNECT_MS * 2 ** Math.max(idleReconnects - 1, 0), SSE_STREAM_MAX_RECONNECT_MS)

/** The attempt only changes the URL so the SSE hook opens a fresh connection; the server ignores it. */
export const withReconnectAttempt = (path: string | undefined, attempt: number) =>
  path !== undefined && attempt > 0 ? `${path}?${new URLSearchParams({ attempt: String(attempt) })}` : path

type Reconnect<T> = {
  path: string | undefined
  attempt: number
  /** Reconnects in a row whose connection dropped without advancing `progress`. */
  idleReconnects: number
  /** The furthest `progressOf` any connection has reached; the next connection resumes after it. */
  progress: number
  /** The events of the connection whose close scheduled this reconnect, to tell its close from the next one's. */
  closedEvents: readonly T[] | undefined
}

const initialReconnect = <T>(path: string | undefined): Reconnect<T> => ({
  path,
  attempt: 0,
  idleReconnects: 0,
  progress: 0,
  closedEvents: undefined,
})

type UseReconnectingSSEStreamOptions<T> = Omit<UseSSEStreamOptions<T>, 'url' | 'headers'> & {
  /** The stream's path; `undefined` stays idle. A new path starts over. */
  path: string | undefined
  /**
   * How far a connection got, as the SSE id of its last event. A reconnect resumes after the furthest any
   * connection reached, with `Last-Event-ID`, and one that does not advance it counts as idle.
   */
  progressOf: (stream: SSEStream<T>) => number
  /** Also reopen a connection that failed, not only one that closed; the SSE hook has already retried it. */
  reconnectsOnError?: boolean
  /** Whether the platform's record of what the stream follows reads as finished, so a close is the end. */
  isRecordFinished: boolean
  /** Re-reads that record: the cached one may predate the platform finishing it, which is what closed the stream. */
  reread: () => void
  /** Reported as the stream's error once it keeps closing without bringing anything new. */
  givenUpError: Error
}

/**
 * An SSE stream that reconnects when it drops while what it follows is still unfinished (a proxy timeout,
 * a deploy). Each reconnect sends `Last-Event-ID`, so the platform resumes after the furthest event any
 * connection delivered; `events` holds only the current connection's. The record is re-read on each drop,
 * and the reconnect is cancelled once it reads as finished. Reconnects that bring nothing new back off, the
 * wait starts over once one does, and after `SSE_STREAM_MAX_IDLE_RECONNECTS` idle ones in a row the stream
 * gives up and reports `givenUpError`.
 */
export const useReconnectingSSEStream = <T>({
  path,
  progressOf,
  reconnectsOnError = false,
  isRecordFinished,
  reread,
  givenUpError,
  ...streamOptions
}: UseReconnectingSSEStreamOptions<T>): SSEStream<T> => {
  const rereadRef = useRef(reread)
  rereadRef.current = reread
  const [reconnect, setReconnect] = useState(() => initialReconnect<T>(path))
  // Forgotten as soon as the path changes, rather than kept for when it changes back: a consumer that returns
  // to a stream starts its fold over, so it must not resume after the events it last saw.
  if (reconnect.path !== path) {
    setReconnect(initialReconnect(path))
  }
  const current = reconnect.path === path ? reconnect : initialReconnect<T>(path)
  const { attempt } = current
  const headers = useMemo(
    () => (current.progress > 0 ? { ...DEFAULT_SSE_HEADERS, 'Last-Event-ID': String(current.progress) } : undefined),
    [current.progress]
  )
  const stream = useSSEStream({ ...streamOptions, url: withReconnectAttempt(path, attempt), headers })
  const { events } = stream
  const { isFinished } = streamOptions
  const hasEnded = useMemo(() => events.some(isFinished), [events, isFinished])
  const reached = progressOf(stream)
  const progress = Math.max(current.progress, reached)
  const idleReconnects = progress > current.progress ? 0 : current.idleReconnects + 1
  const hasDropped =
    stream.connectionStatus === 'finished' || (reconnectsOnError && stream.connectionStatus === 'error')
  const hasClosed = path !== undefined && hasDropped && !hasEnded && events !== current.closedEvents
  const hasGivenUp = hasClosed && !isRecordFinished && idleReconnects > SSE_STREAM_MAX_IDLE_RECONNECTS
  useEffect(() => {
    if (!hasClosed || isRecordFinished || hasGivenUp) {
      return
    }
    rereadRef.current()
    const timer = setTimeout(
      () => setReconnect({ path, attempt: attempt + 1, idleReconnects, progress, closedEvents: events }),
      getSSEStreamReconnectDelay(idleReconnects)
    )
    return () => clearTimeout(timer)
  }, [path, attempt, hasClosed, isRecordFinished, hasGivenUp, idleReconnects, progress, events])
  return hasGivenUp ? { ...stream, connectionStatus: 'error', error: givenUpError } : stream
}
